import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cake, Lock, User, ArrowRight } from 'lucide-react';
import { postRequest } from "../routes/Routes";
import { createUser } from "../auth/Auth";
import { motion } from 'framer-motion';

function LoginPage() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({});
    const navigate = useNavigate();

    const loginFunction = async (event) => {
        setIsLoading(true);
        event.preventDefault();
        let values = {
            email: event.target.email.value,
            password: event.target.password.value,
            endpoint: '/api/login'
        };
        postRequest(values).then(response => {
            if (response.status === 200) {
                if (response.data.error) {
                    setError(values => ({ ...values, error: true, errorMessage: response.data.message }));
                } else {
                    createUser(response.data.user).then(() => {
                        setError(values => ({ ...values, error: false, errorMessage: null }));
                        window.location.href = '/portal';
                    });
                }
            } else {
                setError(values => ({ ...values, error: true, errorMessage: "An error occurred!" }));
            }
            setIsLoading(false);
        });
    };

    return (
        <div className="min-vh-100 d-flex align-items-center justify-content-center px-3">
            <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="w-100"
                style={{ maxWidth: '28rem' }}
            >
                <div className="bg-white rounded-4 shadow-lg overflow-hidden">
                    <div className="gradient-secondary p-4 text-center text-white">
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 150 }}
                        >
                            <Cake className="mx-auto mb-3" size={64} />
                        </motion.div>
                        <h1 className="fs-2 fw-bold mb-2 font-secondary">Keek Portal</h1>
                        <p className="text-white text-opacity-75">Admin Access Required</p>
                    </div>

                    <form onSubmit={loginFunction} className="p-4">
                        <div className="mb-4">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 }}
                            >
                                <label className="form-label fw-medium text-dark">Email</label>
                                <div className="position-relative">
                                    <User
                                        className="position-absolute top-50 translate-middle-y text-muted"
                                        style={{ left: '12px', zIndex: 10 }}
                                        size={20}
                                    />
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        className="form-control form-control-lg rounded-3 ps-5 font-primary"
                                        placeholder="Type your email"
                                        required
                                    />
                                </div>
                            </motion.div>
                        </div>

                        <div className="mb-4">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.4 }}
                            >
                                <label className="form-label fw-medium text-dark">Password</label>
                                <div className="position-relative">
                                    <Lock
                                        className="position-absolute top-50 translate-middle-y text-muted"
                                        style={{ left: '12px', zIndex: 10 }}
                                        size={20}
                                    />
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        className="form-control form-control-lg rounded-3 ps-5 font-primary"
                                        placeholder="Type your password"
                                        required
                                    />
                                </div>
                            </motion.div>
                        </div>

                        {error.error && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="alert alert-danger rounded-3"
                            >
                                {error.errorMessage}
                            </motion.div>
                        )}

                        <div className="mb-4">
                            <motion.button
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.5 }}
                                type="submit"
                                disabled={isLoading}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className="btn gradient-secondary text-white w-100 py-3 rounded-3 fw-semibold d-flex align-items-center justify-content-center gap-2 shadow-lg font-secondary"
                                style={{ opacity: isLoading ? 0.7 : 1 }}
                            >
                                {isLoading ? (
                                    <div className="spinner-border spinner-border-sm text-white" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                ) : (
                                    <>
                                        Sign In
                                        <ArrowRight size={20} />
                                    </>
                                )}
                            </motion.button>
                        </div>

                        <div className="text-center">
                            <p className="small text-muted">
                                Enter your admin email and password to access the portal
                            </p>
                        </div>
                    </form>
                </div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.6 }}
                    className="text-center mt-4"
                >
                    <button
                        onClick={() => navigate('/')}
                        className="btn btn-link text-muted font-primary text-decoration-none"
                    >
                        ← Back to Homepage
                    </button>
                </motion.div>
            </motion.div>
        </div>
    );
}

export default LoginPage;
